import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useEligibilityData } from "../useEligibilityData";
import axios from "axios";
import { assignFundSource } from "../assignFundSource";
import Navbar from "../../../components/molecules/Navbar";
import Footer from "../../../components/molecules/Footer";
import styles from "../index.module.css";
import { ApplicantStage, BankStatementMethod } from "../config";

function EligibilityWaiting() {
  const location = useLocation();
  const navigate = useNavigate();
  const [pageError, setPageError] = useState("");

  const userData = useEligibilityData();

  const creditBureauData = location.state?.creditBureau;
  const fromDigilocker = location.state?.fromDigilocker;
  const fromCreditBureau = location.state?.fromCreditBureau;
  const fromStage10 = location.state?.fromStage10;
  const nextRoute =
    location.state?.nextRoute ||
    (!fromDigilocker && !fromCreditBureau && !fromStage10
      ? sessionStorage.getItem("eligibility_next_route")
      : null);

  useEffect(() => {
    const token = sessionStorage.getItem("eligibility_auth_token");
    const navData = userData;

    // redirect — poll analytics API if bank_statement_method is set
    if (nextRoute) {
      sessionStorage.removeItem("eligibility_next_route");
      const method = sessionStorage.getItem("bank_statement_method");

      const sessionId = sessionStorage.getItem("aa_session_id");
      if (sessionId) {
        let attempts = 0;
        const initiateSession = async () => {
          attempts++;
          try {
            await fetch(
              `${process.env.REACT_APP_DASHBOARD_URL}/bank-statement-analysis/finbox/initiate-session?sessionId=${sessionId}`,
              { method: "GET", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
            );
          } catch {
            if (attempts < 3) initiateSession();
          }
        };
        initiateSession();
      }

      if (method === BankStatementMethod.AA) {
        const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));
        const MAX_POLL_ATTEMPTS = 20;

        const pollOnemoneyAnalytics = async () => {
          const meta = JSON.parse(sessionStorage.getItem("onemoney_meta") || "{}");
          const userId = meta.userId || navData?.userId;
          const applicationId = meta.applicationId || navData?.applicationId;

          for (let i = 0; i < MAX_POLL_ATTEMPTS; i++) {
            if (i > 0) await delay(3000);
            try {
              const res = await fetch(
                `${process.env.REACT_APP_DASHBOARD_URL}/onemoney/analytics/all`,
                {
                  method: "POST",
                  headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                  body: JSON.stringify({ userId, applicationId }),
                }
              );
              const json = await res.json();
              if (
                json?.data?.analyticsData &&
                Array.isArray(json.data.analyticsData) &&
                json.data.analyticsData.length > 0
              ) {
                navigate(nextRoute, { replace: true, state: { data: navData } });
                return;
              }
            } catch (err) {
              console.log(`onemoney analytics poll attempt ${i + 1} failed:`, err);
            }
          }

          setPageError("Bank statement processing is taking longer than expected. Please try uploading again or use a different method.");
        };
        pollOnemoneyAnalytics();
      } else if (method === BankStatementMethod.PDF) {
        const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));
        const MAX_POLL_ATTEMPTS = 20;

        const pollAnalytics = async () => {
          let analyticsReady = false;

          for (let i = 0; i < MAX_POLL_ATTEMPTS; i++) {
            if (i > 0) await delay(3000);
            try {
              const url = `${process.env.REACT_APP_DASHBOARD_URL}/bank-statement-analysis/finbox/admin-analytics-data/all?userId=${navData?.userId}&applicationId=${navData?.applicationId}`;

              const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
              const json = await res.json();

              if (
                json?.data &&
                ((Array.isArray(json.data) && json.data.length > 0) ||
                  (!Array.isArray(json.data) && typeof json.data === "object"))
              ) {
                analyticsReady = true;
                break;
              }
            } catch (err) {
              console.log(`analytics poll attempt ${i + 1} failed:`, err);
            }
          }

          if (!analyticsReady) {
            setPageError("Bank statement processing is taking longer than expected. Please try uploading again or use a different method.");
            return;
          }

          try {
            const evalRes = await fetch(
              `${process.env.REACT_APP_DASHBOARD_URL}/application/evaluate-bs-qec`,
              {
                method: "POST",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                body: JSON.stringify({ userId: navData?.userId, applicationId: navData?.applicationId }),
              }
            );
            const evalData = await evalRes.json();
            const result = evalData?.data?.rulesResult || evalData?.data || "";
            if (result === "red") {
              navigate("/eligibility/rejected", { replace: true, state: { data: navData } });
              return;
            }
            navigate(nextRoute, { replace: true, state: { data: navData } });
          } catch (err) {
            console.log("evaluate-bs-qec failed:", err);
            setPageError("We could not evaluate your bank statement. Please try uploading again or use a different method.");
          }
        };
        pollAnalytics();
      } else {
        const timer = setTimeout(() => {
          navigate(nextRoute, { replace: true, state: { data: navData } });
        }, 3000);
        return () => clearTimeout(timer);
      }
    }

    // stage 10 flow — check summary, route based on address + email
    if (fromStage10) {
      const handleStage10 = async () => {
        const appId = navData?.applicationId;
        const userId = navData?.userId;

        // Step 1: check digilocker for pan
        let hasPan = false;
        try {
          const digiRes = await fetch(
            `${process.env.REACT_APP_DASHBOARD_URL}/digilocker/check-pan?applicationId=${appId}&userId=${userId}`,
            { headers: { Authorization: `Bearer ${token}` }, credentials: "include" }
          );
          const digiJson = await digiRes.json();
          hasPan = digiJson?.data;
        } catch {}

        if (!hasPan) {
          navigate("/eligibility/pan-upload", { replace: true, state: { data: navData } });
          return;
        }

        // Step 2: pan exists — check address and onwards via summary/user
        try {
          const res = await fetch(
            `${process.env.REACT_APP_DASHBOARD_URL}/summary/user?id=${userId}&applicationId=${appId}`,
            { headers: { Authorization: `Bearer ${token}` }, credentials: "include" }
          );
          const result = await res.json();
          const d = result?.data;
            const rawInstId = d.instituteId;
            const instId = Array.isArray(rawInstId) ? rawInstId[0]?.id : rawInstId;
            const courseComplete = instId && d?.courseName?.trim() && d?.courseFees && d?.loanTenure;
            if (!courseComplete) {
              navigate("/eligibility/course-details", { replace: true, state: { data: navData, fromStage10: true } });
              return;
            }
            if (!d?.email?.trim()) {
              navigate("/eligibility/email", { replace: true, state: { data: navData } });
              return;
            }
            assignFundSource(navData, sessionStorage.getItem("eligibility_auth_token"));
            navigate("/eligibility/evaluating", { replace: true, state: { data: navData, email: d.email } });
            return;
        } catch {}

        navigate("/eligibility/course-details", { replace: true, state: { data: navData, fromStage10: true } });
      };
      handleStage10();
      return;
    }

    // digilocker flow — poll until details are ready, then navigate to address
    if (fromDigilocker) {
      const callDigilockerDetails = async () => {
        const appId = navData?.applicationId;
        const userId = navData?.userId;
        const MAX_POLL = 20;
        const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

        let digilockerJson: any = null;

        for (let i = 0; i < MAX_POLL; i++) {
          if (i > 0) await delay(3000);
          try {
            const res = await fetch(
              `${process.env.REACT_APP_DASHBOARD_URL}/digilocker/details?applicationId=${appId}&userId=${userId}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (res.ok) {
              const json = await res.json();
              const aadhaar = json?.data?.response?.response?.aadhaar;
              if (aadhaar && typeof aadhaar === "object") {
                digilockerJson = json;
                break;
              }
            }
          } catch {}
        }

        if (!digilockerJson) {
          navigate("/eligibility/digilocker", { replace: true, state: { data: navData } });
          return;
        }

        const nameMatchScore = digilockerJson?.data?.response?.nameMatchScore;
        if (nameMatchScore !== undefined && nameMatchScore !== null && nameMatchScore !== "" && Number(nameMatchScore) <= 30) {
          navigate("/eligibility/rejected", { replace: true, state: { data: navData } });
          return;
        }

        // Authoritative PAN check — same endpoint the stage-10 resume path
        // below trusts. Deciding this from the raw digilocker payload instead
        // (e.g. presence of a `pan` object) can disagree with this endpoint
        // and send the user to Address first, only to bounce them through
        // pan-upload and back to Address a second time once the stage guard
        // catches the mismatch.
        let hasPan = false;
        try {
          const digiRes = await fetch(
            `${process.env.REACT_APP_DASHBOARD_URL}/digilocker/check-pan?applicationId=${appId}&userId=${userId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const digiJson = await digiRes.json();
          hasPan = digiJson?.data;
        } catch {}

        if (!hasPan) {
          navigate("/eligibility/pan-upload", { replace: true, state: { data: navData } });
          return;
        }

        navigate("/eligibility/waiting", { replace: true, state: { data: navData, fromStage10: true } });
      };
      callDigilockerDetails();
      return;
    }

    // credit bureau flow
    if (fromCreditBureau) {
      const navigateByStage = (stage: number, navData: any) => {
        if (stage === ApplicantStage.PanVerification)       { navigate("/eligibility/income",        { replace: true, state: { data: navData } }); return; }
        else if (stage === ApplicantStage.AddressDetails)   { navigate("/eligibility/bank-statement", { replace: true, state: { data: navData } }); return; }
        else if (stage === ApplicantStage.EmploymentDetails){ navigate("/eligibility/bank-statement", { replace: true, state: { data: navData } }); return; }
        else if (stage === ApplicantStage.BankStatement)    { navigate("/eligibility/waiting",        { replace: true, state: { data: navData, nextRoute: "/eligibility/digilocker" } }); return; }
        else if (stage === ApplicantStage.AwaitingApproval) { navigate("/eligibility/under-review",   { replace: true, state: { data: navData } }); return; }
        else if (stage === ApplicantStage.Approved)         { navigate("/eligibility/congratulations",{ replace: true, state: { data: navData } }); return; }
        else if (stage === ApplicantStage.Digilocker)       { navigate("/eligibility/waiting",        { replace: true, state: { data: navData, fromStage10: true } }); return; }
        else if (stage === ApplicantStage.SanctionTermsAccepted) { navigate("/eligibility/selfie",   { replace: true, state: { data: navData } }); return; }
        else if (stage === ApplicantStage.Selfie)           { navigate("/eligibility/agreement",      { replace: true, state: { data: navData } }); return; }
        else if (stage === ApplicantStage.Agreement)        { navigate("/eligibility/emandate",       { replace: true, state: { data: navData } }); return; }
        else if (stage === ApplicantStage.BsDone)           { navigate("/eligibility/digilocker",     { replace: true, state: { data: navData } }); return; }
        else if (stage === ApplicantStage.FundSourceAssigned){ navigate("/eligibility/evaluating",   { replace: true, state: { data: navData } }); return; }
        else if (stage === ApplicantStage.Rejected)         { navigate("/eligibility/rejected",       { replace: true, state: { data: navData } }); return; }
        else { navigate("/eligibility/income", { replace: true, state: { data: navData } }); }
      };

      const callCreditBureau = async () => {
        const urlData = JSON.parse(sessionStorage.getItem("eligibility_url_data") || "{}");
        try {
          const res = await axios.post(
            `${process.env.REACT_APP_DASHBOARD_URL}/summary/credit-bureau-timble`,
            {
              name: creditBureauData?.name || "",
              mobile: creditBureauData?.mobile || navData?.mobile || "",
              pan: creditBureauData?.pan || "",
              pincode: urlData.pincode || "",
              applicationId: creditBureauData?.applicationId || navData?.applicationId || "",
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          const ruleResult = res?.data?.data?.ruleResult;
          if (ruleResult === "red") {
            navigate("/eligibility/rejected", { replace: true, state: { data: navData } });
            return;
          }

          const appId = creditBureauData?.applicationId || navData?.applicationId;
          if (appId) {
            try {
              const stageRes = await axios.get(
                `${process.env.REACT_APP_DASHBOARD_URL}/admin/application/stage?applicationId=${appId}`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              const stage = stageRes?.data?.data?.stage;
              navigateByStage(stage, navData);
              return;
            } catch (e) {
              console.log("stage error:", e);
            }
          }
          navigate("/eligibility/income", { replace: true, state: { data: navData } });
        } catch (err) {
          console.log("credit-bureau-timble error:", err);
          navigate("/eligibility/income", { replace: true, state: { data: navData } });
        }
      };

      callCreditBureau();
    }
  }, [location.state]);

  const bankMethod = sessionStorage.getItem("bank_statement_method");
  const pageTitle = fromDigilocker ? "Verifying Documents"
    : fromCreditBureau ? "Checking Eligibility"
    : fromStage10 ? "Setting Up Your Profile"
    : (nextRoute && bankMethod === BankStatementMethod.AA) ? "Fetching Bank Data"
    : nextRoute ? "Analyzing Bank Statement"
    : "Please Wait";

  const pageSubtitle = fromDigilocker ? "Please wait while we process your DigiLocker details..."
    : fromCreditBureau ? "Please wait while we check your eligibility..."
    : fromStage10 ? "Fetching your profile details..."
    : (nextRoute && bankMethod === BankStatementMethod.AA) ? "Please wait while we fetch your bank data..."
    : nextRoute ? "Please wait while we analyze your bank statement..."
    : "Please wait a moment...";

  // Non-prod Skip: the next screen for whichever waiting mode this is.
  const skipRoute = fromCreditBureau ? "/eligibility/income"
    : (fromDigilocker || fromStage10) ? "/eligibility/course-details"
    : nextRoute || "/eligibility/course-details";

  return (
    <div className={styles.body}>
      <div className={styles.backdrop}>
        <Navbar />
        <div className={styles.main}>
          <div className={styles.cardWrapper}>
            <div className={styles.container}>

              {/* ── Spinner badge ── */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingTop: "2rem", paddingBottom: "2rem" }}>
                <div
                  style={{
                    width: "72px",
                    height: "72px",
                    borderRadius: "20px",
                    background: "linear-gradient(135deg, #d32028 0%, #a01820 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 8px 24px rgba(211,32,40,0.28)",
                    marginBottom: "1.5rem",
                  }}
                >
                  <svg width="34" height="34" viewBox="0 0 34 34" fill="none" style={{ animation: "spin 1s linear infinite" }}>
                    <circle cx="17" cy="17" r="13" stroke="rgba(255,255,255,0.25)" strokeWidth="3" />
                    <path d="M17 4C24.18 4 30 9.82 30 17" stroke="white" strokeWidth="3" strokeLinecap="round" />
                  </svg>
                </div>

                <p style={{ fontSize: "1.3rem", fontWeight: 800, color: "#1a1a2e", margin: "0 0 0.5rem", fontFamily: "Outfit, sans-serif", textAlign: "center", letterSpacing: "-0.3px" }}>
                  {pageTitle}
                </p>
                <p style={{ fontSize: "0.9rem", color: "#666", margin: 0, textAlign: "center", lineHeight: 1.6, maxWidth: "280px" }}>
                  {pageSubtitle}
                </p>

                {pageError && (
                  <div style={{ marginTop: "1.5rem", textAlign: "center" }}>
                    <p style={{ fontSize: "0.85rem", color: "#d32028", margin: "0 0 1rem", lineHeight: 1.5 }}>{pageError}</p>
                    <button
                      onClick={() => navigate("/eligibility/bank-statement", { replace: true, state: { data: userData } })}
                      style={{
                        padding: "0.65rem 1.5rem",
                        background: "#d32028",
                        color: "#fff",
                        border: "none",
                        borderRadius: "60px",
                        fontSize: "0.88rem",
                        fontWeight: 700,
                        fontFamily: "Outfit, sans-serif",
                        cursor: "pointer",
                      }}
                    >
                      Try Again
                    </button>
                  </div>
                )}

                {process.env.REACT_APP_CURRENT !== 'prod' && (
                  <button
                    onClick={() => navigate(skipRoute, { replace: true, state: { data: userData } })}
                    style={{ marginTop: "1.5rem", background: "none", border: "none", color: "#bbb", fontSize: "0.78rem", cursor: "pointer", fontFamily: "Outfit, sans-serif", textDecoration: "underline" }}
                  >
                    Skip
                  </button>
                )}
              </div>

              <style>{`
                @keyframes spin {
                  from { transform: rotate(0deg); }
                  to { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          </div>
        </div>
         <Footer light />

      </div>
    </div>
  );
}

export default EligibilityWaiting;
